'use client';

import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Copy, RefreshCw, Eye, EyeOff, Github, Link2, Unlink, CheckCircle2, AlertCircle, RefreshCcw } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { getSettings, rotateApiKey, getGitHubAppAuthUrl, unlinkGitHubApp, verifyGitHubConnection } from '@/app/actions/settings';
import { toast } from 'sonner';

export function SettingsTab() {
    const [apiKey, setApiKey] = useState('');
    const [loading, setLoading] = useState(true);
    const [showKey, setShowKey] = useState(false);
    const [generating, setGenerating] = useState(false);
    // GitHub App state
    const [githubAppLinked, setGithubAppLinked] = useState(false);
    const [githubAppUsername, setGithubAppUsername] = useState('');
    const [githubAppConfigured, setGithubAppConfigured] = useState(false);
    const [linkingGithub, setLinkingGithub] = useState(false);
    // GitHub verification state
    const [githubVerified, setGithubVerified] = useState<boolean | null>(null);
    const [verifying, setVerifying] = useState(false);
    const [verificationError, setVerificationError] = useState<string | null>(null);

    useEffect(() => {
        loadSettings();
        // Check for OAuth callback
        handleOAuthCallback();
    }, []);

    const loadSettings = async () => {
        try {
            const settings = await getSettings();
            setApiKey(settings.apiKey || '');
            // GitHub App
            setGithubAppLinked(settings.githubAppLinked || false);
            setGithubAppUsername(settings.githubAppUsername || '');
            setGithubAppConfigured(settings.githubAppConfigured || false);

            // Auto-verify if GitHub is linked
            if (settings.githubAppLinked) {
                handleVerifyGitHub();
            }
        } catch (error) {
            console.error('Failed to load settings:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyGitHub = async () => {
        setVerifying(true);
        setVerificationError(null);
        try {
            const status = await verifyGitHubConnection();
            setGithubVerified(status.valid);
            if (!status.valid && status.error) {
                setVerificationError(status.error);
            }
        } catch (error) {
            console.error('Failed to verify GitHub:', error);
            setGithubVerified(false);
            setVerificationError(error instanceof Error ? error.message : 'Verification failed');
        } finally {
            setVerifying(false);
        }
    };

    const handleOAuthCallback = async () => {
        const params = new URLSearchParams(window.location.search);
        const githubLinked = params.get('github_linked');
        const githubError = params.get('github_error');

        if (githubLinked) {
            // Remove params from URL
            window.history.replaceState({}, '', window.location.pathname);
            setGithubAppLinked(true);
            setGithubAppUsername(githubLinked);
            toast.success(`GitHub account linked: ${githubLinked}`);
            // Reload settings to get fresh data
            loadSettings();
        } else if (githubError) {
            // Remove params from URL
            window.history.replaceState({}, '', window.location.pathname);
            toast.error(githubError);
        }
    };

    const handleLinkGitHub = async () => {
        setLinkingGithub(true);
        try {
            const { authUrl } = await getGitHubAppAuthUrl();
            window.location.href = authUrl;
        } catch (error) {
            console.error('Failed to get auth URL:', error);
            toast.error('Failed to start GitHub authorization');
            setLinkingGithub(false);
        }
    };

    const handleUnlinkGitHub = async () => {
        if (!confirm('Are you sure you want to unlink your GitHub account?')) return;

        setLinkingGithub(true);
        try {
            await unlinkGitHubApp();
            setGithubAppLinked(false);
            setGithubAppUsername('');
            toast.success('GitHub account unlinked');
        } catch (error) {
            console.error('Failed to unlink GitHub:', error);
            toast.error('Failed to unlink GitHub account');
        } finally {
            setLinkingGithub(false);
        }
    };

    const handleRotateKey = async () => {
        if (!confirm('Are you sure? The old API key will stop working immediately.')) return;

        setGenerating(true);
        try {
            const result = await rotateApiKey();
            if (result.success && result.apiKey) {
                setApiKey(result.apiKey);
                // toast.success('New API Key generated');
            }
        } catch (error) {
            console.error(error);
            // toast.error('Failed to generate key');
        } finally {
            setGenerating(false);
        }
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(apiKey);
        // toast.success('Copied to clipboard');
    };

    if (loading) {
        return <div className="flex items-center justify-center h-full"><Spinner /></div>;
    }

    return (
        <div className="space-y-6 max-w-4xl mx-auto">
            <Card>
                <CardHeader>
                    <CardTitle>API Access</CardTitle>
                    <CardDescription>
                        Generate an API Key to run tests or interact with the agent headlessly.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="api-key">API Key</Label>
                        <div className="flex gap-2">
                            <div className="relative flex-1">
                                <Input
                                    id="api-key"
                                    type={showKey ? "text" : "password"}
                                    value={apiKey}
                                    readOnly
                                    placeholder="No API Key generated yet"
                                    className="pr-10"
                                />
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                    onClick={() => setShowKey(!showKey)}
                                >
                                    {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </Button>
                            </div>
                            <Button variant="outline" size="icon" onClick={copyToClipboard} disabled={!apiKey}>
                                <Copy className="h-4 w-4" />
                            </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            Pass this key in the <code>x-api-key</code> header or <code>Authorization: Bearer</code> header.
                        </p>
                    </div>
                    <Button onClick={handleRotateKey} disabled={generating}>
                        {generating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {apiKey ? 'Rotate API Key' : 'Generate API Key'}
                    </Button>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Github className="h-5 w-5" />
                        GitHub Account
                    </CardTitle>
                    <CardDescription>
                        Link your GitHub account to access private repositories and create projects.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {githubAppConfigured ? (
                        githubAppLinked ? (
                            <div className="space-y-4">
                                <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                                        <Github className="h-5 w-5" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-medium">{githubAppUsername}</p>
                                        <div className="flex items-center gap-2 text-sm">
                                            {verifying ? (
                                                <>
                                                    <Loader2 className="h-3 w-3 animate-spin" />
                                                    <span className="text-muted-foreground">Verifying...</span>
                                                </>
                                            ) : githubVerified === true ? (
                                                <>
                                                    <CheckCircle2 className="h-3 w-3 text-green-500" />
                                                    <span className="text-green-600">Connection verified</span>
                                                </>
                                            ) : githubVerified === false ? (
                                                <>
                                                    <AlertCircle className="h-3 w-3 text-red-500" />
                                                    <span className="text-red-600">Connection issue</span>
                                                </>
                                            ) : (
                                                <span className="text-muted-foreground">GitHub account linked</span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Show verification error message */}
                                {githubVerified === false && verificationError && (
                                    <div className="p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
                                        <p className="text-sm text-red-600 dark:text-red-400">{verificationError}</p>
                                    </div>
                                )}

                                <div className="flex flex-wrap gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleVerifyGitHub}
                                        disabled={verifying}
                                    >
                                        {verifying ? (
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        ) : (
                                            <RefreshCcw className="mr-2 h-4 w-4" />
                                        )}
                                        Verify Connection
                                    </Button>

                                    {/* Show Re-link button when verification fails */}
                                    {githubVerified === false && (
                                        <Button
                                            variant="default"
                                            size="sm"
                                            onClick={handleLinkGitHub}
                                            disabled={linkingGithub}
                                        >
                                            {linkingGithub && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                            <Link2 className="mr-2 h-4 w-4" />
                                            Re-link Account
                                        </Button>
                                    )}

                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleUnlinkGitHub}
                                        disabled={linkingGithub}
                                    >
                                        {linkingGithub && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                        <Unlink className="mr-2 h-4 w-4" />
                                        Unlink
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <p className="text-sm text-muted-foreground">
                                    Connect your GitHub account to enable repository access and project creation.
                                </p>
                                <Button onClick={handleLinkGitHub} disabled={linkingGithub}>
                                    {linkingGithub && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                    <Link2 className="mr-2 h-4 w-4" />
                                    Link GitHub Account
                                </Button>
                            </div>
                        )
                    ) : (
                        <p className="text-sm text-muted-foreground">
                            GitHub App integration is not configured. Contact administrator.
                        </p>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
